<script>
  import { sum, timeFormat } from "d3";
  import { onMount } from "svelte";
  import param from "../utils/urlParam.js";
  import embedCode from "../utils/embed.js";

  import Meta from "./Meta.svelte";
  import Header from "./Header.svelte";
  import Rank from "./Rank.svelte";
  import Method from "./Method.svelte";
  import Description from "./Description.svelte";
  import ButtonSet from "./helpers/ButtonSet.svelte";
  import Download from "./Download.svelte";

  import rawCountries from "../data/countries.csv";
  import copy from "../data/doc.json";

  const countries = rawCountries.map((d) => ({
    ...d,
    iso2: d.iso2.toLowerCase(),
    population: +d.population,
    gdp: +d.gdp,
    olympicsIndex: +d.olympics_index,
    sportsIndex: +d.sports_index,
  }));

  let medalData = [];
  let data = [];
  let downloadData = [];
  let metric = "total";
  let embed;
  let updated;

  const url = `https://polygraph-pub.s3.amazonaws.com/daily_country_medals.json?version=${Date.now()}`;

  $: if (medalData.length) {
    data = countries
      .map((d) => {
        const match = medalData.find((m) => m["country_iso3"] === d.ioc) || {};
        return {
          ...d,
          name_olympics: match.country_full,
          total: match.total,
          gold: match.gold,
          silver: match.silver,
          bronze: match.bronze,
        };
      })
      .filter((d) => d.total);

    downloadData = data.map((d) => {
      const c = { ...d };
      delete c.iso2;
      delete c.name_orig;
      delete c.name_alt;
      delete c.population_index;
      delete c.gdp_index;
      delete c.name_olympics;
      delete c.diet_index;
      return c;
    });
  }

  onMount(async () => {
    embed = !!param.get("embed");

    try {
      const response = await fetch(url);
      const raw = await response.json();
      medalData = raw.data;
      const date = new Date(+raw.updated * 1000);
      updated = timeFormat("%b %d, %Y %I:%M %p")(date);
    } catch (err) {
      console.log(err);
    }
  });
</script>

<Meta copy="{copy}" />

{#if !embed}
  <Header />
{/if}

<div class="story" class:embed>
  <div class="intro">
    <p class="overline">{copy.overline}</p>
    <h1 class="hed">{copy.hed}</h1>
    <p class="dek">{copy.dek}</p>
    <p class="updated">Last Updated: {updated}</p>
  </div>

  <section id="rank">
    <ButtonSet options="{copy.metrics}" bind:value="{metric}" />
    <Description options="{copy.metrics}" metric="{metric}" />
    {#if data.length}
      <Rank data="{data}" metric="{metric}" />
    {/if}
    {#if !embed}
      <div class="embed">
        <p>{copy.embed}</p>
        <textarea
          value="&lt;iframe&nbsp;src=&quot;https://olympics-trends.appspot.com/?embed=true&quot;&nbsp;width=&quot;100%&quot;&nbsp;height=&quot;480px&quot;&nbsp;style=&quot;border:&nbsp;1px&nbsp;solid&nbsp;#ccc;&quot;&nbsp;/&gt;"
        ></textarea>
      </div>
    {/if}

    <Download title="gni-olympics" data="{downloadData}" />
  </section>

  {#if !embed}
    <section id="method">
      <Method />
    </section>
  {/if}
</div>

<style>
  .story {
    max-width: 80em;
    margin: 0 auto;
    padding: 1em;
    font-family: "Inter", sans-serif;
  }

  .intro {
    text-align: center;
    margin: 1em 0;
  }

  .hed {
    font-size: 2em;
    margin: 0;
    font-weight: 900;
  }

  .dek {
    font-size: 1em;
    margin: 1em 0;
  }

  .overline {
    background-color: var(--fg);
    color: var(--bg);
    display: inline-block;
    padding: 0.5em;
  }

  .overline,
  .updated {
    font-size: 0.75em;
  }

  section {
    margin: 2em 0;
    padding: 2em 0;
  }

  #rank {
    min-height: 480px;
  }

  .embed {
    width: 100%;
    max-width: 300px;
    margin: 1em auto;
    text-align: center;
  }

  textarea {
    font-size: 14px;
    width: 100%;
    height: 6em;
    padding: 0.5em;
    margin-top: 0.5em;
    background-color: var(--fg);
    color: var(--bg);
    border: none;
    overflow: hidden;
    resize: none;
  }

  @media only screen and (min-width: 480px) {
    .overline {
      font-size: 0.85em;
    }

    .hed {
      font-size: 3em;
    }

    .dek {
      font-size: 1.5em;
    }

    .updated {
      font-size: 0.85em;
    }
  }

  @media only screen and (min-width: 720px) {
    .hed {
      font-size: 4em;
    }
  }
</style>
